import { Component, OnInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';

import { PayAccount } from '@app/modules/model/payaccount';
import { TicketList } from '@app/modules/model/payaccount';
import { Ticket } from '@app/modules/model/ticket';
import { Business } from '@app/modules/model/business';
import { AccountInvoice } from '@app/modules/model/accountinvoice';
import { TicketInvoice } from '@app/modules/model/ticketinvoice';

import { AppConstants } from '@app/app.constants';
import { alertService } from '@app/modules/common/alert';
import { toastService } from '@app/modules/common/toast';
import { languageSupport, languageService } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { UiSummaryModal } from '@app/components/login/modal/summary/ui-summary-modal';
import { UiTicketModal } from '@app/components/login/modal/ticket/ui-ticket-modal';
import { UiBusinessEditChange } from '@app/components/model/client/ui-edit/ui-edit';

/************************************/
/* ACCOUNT EDIT                     */
/************************************/

@Component({
    selector: 'ui-payaccount-edit',
    templateUrl:'./ui-edit.html',
    styleUrls: [ 
        './ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPayAccountEdit extends languageSupport implements OnInit, OnDestroy {
    @Input('account') _account: PayAccount = null;

    get _place(){
        return this._account.place;
    }

    get IsOnline(){
        return this.view.IsOnline;
    }

    private _isloading = true;
    get IsLoading(){
        return this.IsOnline && this._isloading;
    }

    set IsLoading(value){
        this._isloading = value;
        this.change.markForCheck();
    }

    private _placetickets = new TicketList ();
    private get Tickets(){
        return this._placetickets;
    }

    private _merchant = null;
    get _business(){
        if (this._merchant == null){
            this._merchant = new Business(null, this.data);

            let _business = this.AccountForm.get('client').value;
            if (_business){
                this._merchant.name = _business.name;
                this._merchant.address = _business.address;
                this._merchant.taxid = _business.taxid;
                this._merchant.postalcode = _business.postalcode;
                this._merchant.region = _business.region;
            }
        }

        return this._merchant;
    }

    get _user(){
        // Usuario EFECTIVO (camarero del PIN / principal bloqueado), no data.user: si
        // no, un camarero sin ClientAdd en sesión del owner podría editar cuentas.
        return this.data.EffectiveUser;
    }

    get CanEdit(){
        if (!this._place || !this._user){
            return false;
        }
        return this._place.IsOwner(this._user) || this._place.AllowClientAdd(this._user);
    }

    private _editmode = false;
    get EditMode(){
        return this._editmode;
    }

    set EditMode(value){
        this._editmode = value;
        this.change.markForCheck();
    }

    get CanRemove(){
        // Depende del PERMISO EFECTIVO (CanEdit), no solo de EditMode/saldo: si durante
        // la edición se identifica otro camarero sin ClientAdd, no debe poder borrar.
        return this.CanEdit && this.EditMode && !this._account.ToInsert && (this.AmountPending == 0);
    }

    get CanBlock(){
        return (this._account.IsBlocked == false);
    }

    Price(_price){
        let _parts = (Math.abs(_price)).toString().split('.');

        let _int = _parts[0];
        let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
        let _str = _int + ',' + _dec;
        let _neg = (_price < 0); 

        return {
            Int: _int,
            Dec: _dec,
            Str: _str,
            Neg: _neg
        }    
    }

    DateStr(_date){
        let _dd = ('00' + _date.getDate()).slice(-2);
        let _MM = ('00' + (_date.getMonth()+1)).slice(-2);
        let _yy = _date.getFullYear()

        return _dd + "/" + _MM + "/" + _yy;
    }

    private _accountForm = null;   
    private LoadAccountForm(){
        let _name = this._account.name;
        let _client = this._account.client;

        if (!this._accountForm){
            this._accountForm = new FormGroup({
                name: new FormControl(_name, [
                    Validators.required 
                ]),
                client: new FormControl(_client, [
                    // no validators required
                ])
            });
        }
        else {
            this._accountForm.patchValue({
                name: _name, 
                client: _client
            }); 
        }
            
        this.OnBusinessSelect();

        this._accountForm.markAsPristine();      
        setTimeout(() => {
            this._accountForm.markAsPristine();      
        }, 0);
    }

    get AccountForm(){
        if (this._accountForm == null){
            this.LoadAccountForm();
        }
        return this._accountForm;
    }

    private _showpaid = false;
    get ShowPaidList(){
        return this._showpaid;
    }

    set ShowPaidList(value){
        this._showpaid = value;
        this.change.markForCheck();
    }

    private _showpending = false;
    get ShowPendingList(){
        return this._showpending;
    }

    set ShowPendingList(value){
        this._showpending = value;
        this.change.markForCheck();
    }

    private _payments = null;
    get Payments(){
        if (this._payments == null){
            let _ini = new Date();
            _ini.setFullYear(_ini.getFullYear() - 1);

            let _invoices = [];     // limit to last year invoices
            for (let _invoice of this._account.invoices){
                if (_invoice.updated.getTime() >= _ini.getTime()){
                    _invoices.push(_invoice);
                } 
            }
    
            let _payments = _invoices.sort((a, b) => {
                return b.until.getTime() - a.until.getTime();
            });
            this._payments = _payments;
        }
        return this._payments;
    }

    private _paidtickets = null;
    get PaidTickets(){
        if (this._paidtickets == null){
            let _tickets = [];
            for(let _ticket of this.Tickets){
                if ((_ticket.IsToPay != true) || (_ticket.price == 0)){
                    _tickets.push(_ticket);
                }
            }

            _tickets = _tickets.sort((a, b) => {
                return b.created.getTime() - a.created.getTime(); 
            });                

            this._hidepaid = (_tickets.length <= this._lastpaid);                    
            this._paidtickets = _tickets;            
        }
        return this._paidtickets;
    }

    get AmountPaid(){
        let _price = 0;
        for(let _ticket of this.PaidTickets){
            _price += _ticket.price;
        }
        return _price;
    }

    get AveragePaid(){
        let _average = 0;

        let _length = this.PaidTickets.length;
        if (_length > 0){
            _average = this.AmountPaid / _length; 
        }

        return Math.round(_average * 100) / 100;
    }

    private _paidtable = null;
    get PaidTable(){
        if (this._paidtable == null){
            let _rows = [];

            for(let _ticket of this.PaidTickets){
                _rows.push({
                    type: 'ticket',
                    date: _ticket.created.getTime(),
                    data: _ticket
                });

                // add a row for the tickets with invoice (> InvoiceMaxPrice)
                if (_ticket.invoice != null){
                    let _invoice = _ticket.invoice;
                    _rows.push({
                        type: 'invoice',
                        date: _invoice.created.getTime(),
                        data: _ticket
                    });
                }
            }

            for (let _payment of this.Payments){
                _rows.push({
                    type: 'payment',
                    date: _payment.until.getTime(),
                    data: _payment
                });
            }
            
            _rows = _rows.sort((a, b) => {
                if (a.date == b.date){
                    if (a.type == b.type){
                        return b.objid - a.objid;
                    }

                    return (a.type == 'ticket') ? 1 : -1;
                }

                return b.date - a.date;
            })
    
            this._paidtable = _rows;    
        }

        return this._paidtable;
    }

    get SincePaid(){        
        let _last = new Date();
        let _init = new Date()

        _init.setFullYear(_init.getFullYear() - 1);

        // get the date of the first pending ticket
        if (this.PendingTickets.length > 0){
            _last = this.PendingTickets[0].created;
        }

        // get the date of the first paid ticket
        if (this.PaidTickets.length > 0){
            _last = this.PaidTickets.slice(-1)[0].created;
        }

        if (_last.getTime() > _init.getTime()){
            return this.DateStr(_last);
        }
        else {
            return this.DateStr(_init);
        }
    }

    private _pendtickets = null;
    get PendingTickets(){
        if (this._pendtickets == null){
            let _tickets = [];
            for(let _ticket of this.Tickets){
                if (_ticket.IsToPay == true){
                    _tickets.push(_ticket);
                }
            }

            _tickets = _tickets.sort((a, b) => {
                return a.created.getTime() - b.created.getTime(); 
            });                

            this._hidepend = (_tickets.length <= this._lastpend);                     
            this._pendtickets = _tickets;            
        }
        return this._pendtickets;
    }

    private _ticketmap = new Map<string, Ticket>();
    private async GetTicketItems(objids){
        let _tickets = [];
        let _objids = objids.slice(0);

        for (let _idx = _objids.length-1; _idx >= 0; _idx--){
            let _ticket = this._ticketmap.get(_objids[_idx]);
            if (_ticket){
                _tickets.push(_ticket);
                _objids.splice(_idx, 1);
            }
        }

        let _server = await this.reports.TicketObjects(_objids);
        for (let _ticket of _server){
            this._ticketmap.set(_ticket.objid, _ticket);
            _tickets.push(_ticket);
        }

        _tickets.sort((a, b) => {
            return a.created.getTime() - b.created.getTime();
        })

        return _tickets;
    }

    private async _AddProformaInvoice(_info){
        let _invoice = new AccountInvoice(null, this.data, { volatile: true });
        if (_invoice){
            _invoice.account = this._account,
            _invoice.since = new Date(_info.since),
            _invoice.until = new Date(_info.until),
            _invoice.amount = Math.round(_info.amount * 100)/100,
            _invoice.payment = null
        }

        if (_invoice){
            _info['proforma'] = _invoice;
            for (let _ticket of _info.tickets){
                _invoice.AddTicket(_ticket);
            }
        }

        return _invoice;
    }    

    private async _ProformaInvoices(){
        let _proforma = [];
        let _invoice = null;

        let _tickets = this.PendingTickets;

        // completely load the ticket to be paid 
        let _objids = [];
        for (let _ticket of _tickets){
            _objids.push(_ticket.objid)
        }

        _tickets = await this.GetTicketItems(_objids);
        for(let _ticket of _tickets){
            let _addinvoice = false;

            // if ticket amount exceedes InvoiceMaxPrice then must have an invoice
            if (_ticket.ToInvoice){
                let _ticketinvoice = new TicketInvoice(null, this.data);
                if (_ticketinvoice){
                    _ticketinvoice.ticket = _ticket;
                    _ticketinvoice.client = this._account.client;
                    _ticketinvoice.status = 'AC';
                }

                _proforma.push(_ticketinvoice);
                continue;   // this ticket will not go to a summary invoice
            }

            if (_invoice == null){
                _invoice = this._InvoiceInfo(_ticket);
            }

            let _date = new Date(_invoice.since);
            let _next = _ticket.created;
            let _amount = _invoice.amount + _ticket.price;

            // create a new invoice when ticket is in different month
            if (!_addinvoice && ((_next.getFullYear() != _date.getFullYear()) || (_next.getMonth() != _date.getMonth()))){
                _addinvoice = true;
            }

            // create a new invoice when total amount exceedes InvoiceMaxPrice
            if (!_addinvoice && (_amount >= AppConstants.InvoiceMaxPrice)){
                _addinvoice = true;
            }

            // prepare next invoice if cannot be included in current one
            if (_addinvoice){  
                this._AddProformaInvoice(_invoice);
                _proforma.push(_invoice);
                _invoice = this._InvoiceInfo(_ticket);
            }

            _invoice.amount += _ticket.price;
            _invoice.until = Math.max(_next.getTime(), _invoice.until);
            _invoice.tickets.push(_ticket);
        }

        // create the _current (last) invoice
        if (_invoice){
            this._AddProformaInvoice(_invoice);
            _proforma.push(_invoice);
        }

        return _proforma;
    }

    private _pendingtable = null;
    get PendingTable(){
        if (this._pendingtable == null){
            this._pendingtable = [];    // avoid multiple entries
            
            let _rows = [];

            this._ProformaInvoices().then(
            _proforma => {
                for(let _ticket of _proforma){
                    _rows.push({
                        type: 'proforma',
                        date: new Date().getTime(),
                        data: _ticket.proforma
                    });
                }

                for(let _ticket of this.PendingTickets){
                    _rows.push({
                        type: 'ticket',
                        date: _ticket.created.getTime(),
                        data: _ticket
                    });
                }
                
                _rows = _rows.sort((a, b) => {
                    if (a.date == b.date){
                        if (a.type == b.type){
                            return b.objid - a.objid;
                        }
    
                        return (a.type == 'ticket') ? 1 : -1;
                    }
    
                    return b.date - a.date;
                })
        
                this._pendingtable = _rows;  
                this.change.markForCheck();                      
            });
        }

        return this._pendingtable;
    }

    get AmountPending(){
        let _price = 0;
        for(let _ticket of this.PendingTickets){
            _price += _ticket.price;
        }
        return _price;
    }

    get AveragePending(){
        let _average = 0;

        let _length = this.PendingTickets.length;
        if (_length > 0){
            _average = this.AmountPending / _length; 
        }
        
        return Math.round(_average * 100) / 100;
    }

    get SincePending(){
        let _last = new Date();

        // get the date of the last payment
        if (this.Payments.length > 0){
            _last = this.Payments[0].until;
        }

        // last paid is the date of the first pending
        if (this.PendingTickets.length > 0){
            _last = this.PendingTickets.slice(-1)[0].created;
        }

        return this.DateStr(_last);
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    private get reports(){
        return this.data.Reports;
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private toast: toastService, private alertCtrl: alertService, private modalCtrl: ModalController, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _CanClose(){
        return new Promise(async (resolve) => {
            if (this.AccountForm.dirty){
                console.log("[EDIT ACCOUNT] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        this.LoadAccountForm();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });  
    }

    private _OnRefreshForm(){
        this._pendingtable = null;
        this._paidtable = null;

        this._lastpend = this._stepsize;
        this._lastpaid = this._stepsize;    

        this._pendtickets = null;
        this._paidtickets = null;

        this._payments = null;
        if (this.AccountForm.pristine){
            this.LoadAccountForm();
        }

        this.change.markForCheck();
    }

    private _OnRefreshAccount(){
        let _tickets = this._place.tickets;
        for(let _ticket of _tickets){
            if (_ticket.account == this._account){
                (_ticket.IsValid) ? this._placetickets.add(_ticket) : this._placetickets.del(_ticket);
            }
        }

        this._OnRefreshForm();
    }    

    private async LoadAccountTickets(){
        let _tickets = await this.reports.AccountTickets(this._account);
        for(let _ticket of _tickets){
            this._placetickets.add(_ticket);
        }

        this._OnRefreshAccount();
    }

    private _close_subscription = null;
    private _account_subscription = null;
    private _place_subscriptions = [];
    private _online_subscription = null;
    private _theme_subscription = null;

    async ngOnInit(){
       this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            let _allow = await this._CanClose();
            this.view.OnCloseResponse(_allow);

            if (_allow){
                this.OnClose();
            }
        });
        
        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });

        this._online_subscription = this.view.OnOnline.subscribe(
        data => {
            this.change.markForCheck();
        });
    
        this._account_subscription = this._account.OnRefresh.subscribe(
        data => {
            this.change.markForCheck();
        });        

        if (this._place){
            this.IsLoading = true;
            await this.LoadAccountTickets();
            this.IsLoading = false;            

            this._place_subscriptions.push(
                this._place.OnTargetRefresh('TICKET').subscribe(
                data => {
                    this._OnRefreshAccount();
                })
            );

            this._place_subscriptions.push(
                this._place.OnTargetRefresh('CLIENT').subscribe(
                data => {
                    this._OnRefreshAccount();
                })
            );
        }

        this.EditMode = this._account.ToInsert;
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        if (this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        if (this._account_subscription){
            this._account_subscription.unsubscribe();
            this._account_subscription = null;
        }

        for (let _subscription of this._place_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._place_subscriptions = [];

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/    

    OnBusinessSelect(){
        let _control = this.AccountForm.get('client');
        if (_control){
            _control.setErrors(null);
            
            let _business = _control.value;
            if (_business){
                this._business.name = _business.name;
                this._business.taxid = _business.taxid;
                this._business.email = _business.email;
                this._business.address = _business.address;
                this._business.postalcode = _business.postalcode;
                this._business.region = _business.region;
            }
            _control.value = _business;
        }

        this.change.markForCheck();
    }

    OnBussinessChange(ev: UiBusinessEditChange){
        let _control = this.AccountForm.get('client');
        if (_control){
            _control.setErrors(null);
            if (!ev.formValid){
                _control.setErrors({ 'invalid': true });
            }
        }

        this.AccountForm.markAsDirty();
        this.change.markForCheck();
    }

    @Output('onClose') _onClose = new EventEmitter<any>();         
    OnClose(){
        this._onClose.emit();
    }

    OnEdition(){
        if (this.CanEdit){
            this.EditMode = true;
        }
    }

    OnBlock(){
        // El control de bloqueo es SOLO-OWNER en la plantilla (*ngIf IsOwner). Re-chequea
        // IsOwner con el usuario EFECTIVO —no CanEdit, que además incluye AllowClientAdd—
        // para usar la misma regla de autorización que la UI aunque el camarero cambie.
        if (!this._place || !this._user || !this._place.IsOwner(this._user)){
            return;
        }
        this._account.status = (this._account.IsBlocked) ? 'AC' : 'BL';
        this._account.DoCommit();

        this.change.markForCheck();
    }

    OnShowPaidTickets(){
        this.ShowPaidList = !this.ShowPaidList;
    }

    OnShowPendingTickets(){
        this.ShowPendingList = !this.ShowPendingList;
    }

    private _OnDelete(){
        this._account.status = 'DE';
        this._place.DelPayAccount(this._account);
        this._account.DoCommit();

        this.OnClose();
    }

    async OnDelete(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_account_delete_confirm'),
            message: this.lang.tr('@place_account_delete_message', [ this._account.name || this.lang.tr('@place_account_no_name') ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                // Re-chequeo al RESOLVER la confirmación: el camarero efectivo pudo cambiar
                // mientras el diálogo estaba abierto (otro PIN / bloqueo). CanRemove incluye
                // CanEdit, así que cubre permiso + EditMode + saldo pendiente.
                if (!this.CanRemove){
                    return;
                }
                this._OnDelete();
            }
        });

        await alert.present();
    }

    private _CreateBusiness(){
        let _business: Business = null;

        let _merchantname = this._business.name;
        let _merchanttaxid = this._business.taxid;
        let _merchantemail = this._business.email;
        let _merchantaddress = this._business.address;

        for (let _client of this._account.place.clients){
            if (_client.IsValid && (_client.name == _merchantname) && (_client.taxid == _merchanttaxid)){
                _business = _client;
            }
        }
        
        // create new business on address change
        if (_business && (_business.address != _merchantaddress)){
            _business.status = 'DE';
            _business.DoCommit();       
            _business = null;
        }

        // business not found: use the created one
        if (!_business) {
            _business = this._business;
            if (_business){
                _business.place = this._account.place;
                _business.name = _merchantname;
                _business.taxid = _merchanttaxid;
                _business.email = _merchantemail;
                _business.address = _merchantaddress;
                _business.status = 'AC';
            }
        }
        // otherwise update the business information
        else {
            _business.name = _merchantname;
            _business.taxid = _merchanttaxid;
            _business.email = _merchantemail;
        }
        
        return _business;
    }

    async OnAccept(){
        // Re-chequear permiso al GUARDAR (no solo al entrar en edición): el usuario
        // efectivo pudo cambiar durante la edición (cambio de camarero / bloqueo).
        if (!this.CanEdit){
            return;
        }
        if (this.AccountForm.valid){
            this._account.name = this.AccountForm.get('name').value;
            this._account.client = this._CreateBusiness();

            if (await this._account.DoCommit()){
                this._place.AddPayAccount(this._account);
                this.OnClose();
            }
        }
    }

    OnCancel(){
        if (this.EditMode && !this._account.ToInsert){
            this.EditMode = false;
        }
        else {
            this.OnClose();
        }
    }

    private _InvoiceInfo(_ticket){
        return {
            since: _ticket.created.getTime(),
            until: _ticket.created.getTime(),
            amount: 0,   
            tickets: [],         
        };
    }

    // Facturas creadas durante el _OnPayAccount EN CURSO — para poder
    // deshacerlas en memoria si un _AddAccountInvoice posterior lanza
    // (contador ilegible, fail-loud de SetInvoiceNumber). Sin el rollback,
    // una AccountInvoice huerfana con _toInsert=true quedaba colgada del
    // account y el retry del usuario la insertaba en DDBB como factura
    // fiscal espuria con numero consumido.
    private _paymentInvoices: Array<AccountInvoice> = [];

    private async _AddAccountInvoice(_info, _payment){
        let _invoice = new AccountInvoice(null, this.data);
        if (_invoice){
            _invoice.account = this._account,
            _invoice.since = new Date(_info.since),
            _invoice.until = new Date(_info.until),
            _invoice.amount = Math.round(_info.amount * 100)/100,
            _invoice.payment = _payment
        }

        if (_invoice){
            await _invoice.SetInvoiceNumber();
            this._account.AddInvoice(_invoice);
            this._paymentInvoices.push(_invoice);

            for (let _ticket of _info.tickets){
                _invoice.AddTicket(_ticket);

                _ticket.invceac = _invoice;
                _ticket.payment = _payment;
            }

            await _invoice.AddVeriFactu();
            await _invoice.AddTicketBAI();
        }

        return _invoice;
    }

    private _accountpayment = false;
    get AccountPayment(){
        return this._accountpayment;
    }

    set AccountPayment(value){
        this._accountpayment = value;
        
        if (value){
            this.toast.ShowWait('@place_account_paying_tickets', 20000);
        }
        else {
            this.toast.HideWait();
        }

        this.change.markForCheck();
    }

    private async _OnPayAccount(payment){
        let _invoice = null;

        this.AccountPayment = true;

        // temporally unregister from place subscriptions
        for (let _subscription of this._place_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._place_subscriptions = [];

        // try/catch/finally: SetInvoiceNumber puede lanzar (contador de
        // tickets ilegible — fail-loud de store.GetLastTicket). Sin esta
        // proteccion, la excepcion escapaba con las suscripciones del place
        // desregistradas y AccountPayment=true → vista congelada + spinner
        // de espera infinito hasta recargar la app.
        this._paymentInvoices = [];     // tracking para rollback en error

        try {
            let _tickets = this.PendingTickets;

            // completely load the ticket to be paid
            let _objids = [];
            for (let _ticket of _tickets){
                _objids.push(_ticket.objid)
            }

            _tickets = await this.GetTicketItems(_objids);
            for(let _ticket of _tickets){
                let _addinvoice = false;

                // if ticket amount exceedes InvoiceMaxPrice then must have an invoice
                if (_ticket.ToInvoice){
                    let _ticketinvoice = new TicketInvoice(null, this.data);
                    if (_ticketinvoice){
                        _ticketinvoice.ticket = _ticket;
                        _ticketinvoice.client = this._account.client;
                        _ticketinvoice.status = 'AC';
                    }

                    _ticket.invoice = _ticketinvoice;
                    continue;   // this ticket will not go to a summary invoice
                }

                if (_invoice == null){
                    _invoice = this._InvoiceInfo(_ticket);
                }

                let _date = new Date(_invoice.since);
                let _next = _ticket.created;
                let _amount = _invoice.amount + _ticket.price;

                // create a new invoice when ticket is in different month
                if (!_addinvoice && ((_next.getFullYear() != _date.getFullYear()) || (_next.getMonth() != _date.getMonth()))){
                    _addinvoice = true;
                }

                // create a new invoice when total amount exceedes InvoiceMaxPrice
                if (!_addinvoice && (_amount >= AppConstants.InvoiceMaxPrice)){
                    _addinvoice = true;
                }

                // prepare next invoice if cannot be included in current one
                if (_addinvoice){
                    await this._AddAccountInvoice(_invoice, payment);
                    _invoice = this._InvoiceInfo(_ticket);
                }

                _invoice.amount += _ticket.price;
                _invoice.until = Math.max(_next.getTime(), _invoice.until);
                _invoice.tickets.push(_ticket);
            }

            // create the _current (last) invoice
            if (_invoice){
                await this._AddAccountInvoice(_invoice, payment);
            }

            // update the tickets and insert the invoices
            let _transaction = await this.data.BeginTransaction();
            if (_transaction){
                _transaction.SendCommit(this._account);
                for(let _ticket of _tickets){
                    _transaction.SendCommit(_ticket);
                }

                await _transaction.Flush(true);
            }

            this.reports.ResetAccount(this._account);
        }
        catch(e){
            console.error("[PAYACC] Pago de cuenta abortado:", e);

            // Rollback en memoria: desvincular del account las facturas
            // creadas en ESTE intento. Sin esto, el retry del usuario
            // arrastraba las huerfanas (_toInsert=true) al SendCommit del
            // account → facturas fiscales espurias en DDBB. Los numeros de
            // factura consumidos se resincronizan desde el server en el
            // siguiente GetLastServed.
            for (let _orphan of this._paymentInvoices){
                try {
                    this._account.DelInvoice(_orphan);
                }
                catch(_e){
                    console.error("[PAYACC] rollback DelInvoice fallo:", _e);
                }
            }
            this._paymentInvoices = [];

            this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_1'));
        }
        finally {
            // SIEMPRE (exito o error): recuperar las suscripciones del place
            // y cerrar el estado de pago — sin esto la vista dejaba de
            // refrescar y el toast de espera no se cerraba nunca.
            this._place_subscriptions.push(
                this._place.OnTargetRefresh('TICKET').subscribe(
                data => {
                    this._OnRefreshAccount();
                })
            );

            this._place_subscriptions.push(
                this._place.OnTargetRefresh('CLIENT').subscribe(
                data => {
                    this._OnRefreshAccount();
                })
            );

            setTimeout(() => {  // margin for tickets update
                this.LoadAccountTickets();
                this.AccountPayment = false;
            }, 1000);
        }
    }

    async OnPayAccount(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_account_dopay_confirm_title'),
            message: this.lang.tr('@place_account_dopay_confirm_message', [ this.AmountPending.toFixed(2), this.lang.tr('@currency') ]),
            backdropDismiss: false,
            inputs: [{
                type: 'radiogroup',
                name: 'payment',
            }, {
                type: 'radiobutton',
                name: 'payment',
                label: this.lang.tr('@place_account_dopay_confirm_paycash'),
                value: 'PAYCASH'
            }, {
                type: 'radiobutton',
                name: 'payment',
                label: this.lang.tr('@place_account_dopay_confirm_paycard'),
                value: 'PAYCARD'
            }],
            buttons: [{
                text: this.lang.tr('@yes'),
                check: (_data) => {
                    return (_data['payment'] != null);
                },
                handler: (data) => {
                    setTimeout(() => {
                        alert.dismiss(data);
                    }, 100);

                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);

                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail.data){
                this._OnPayAccount(detail.data['payment']);
            }
        });

        await alert.present();        
    }

    /* ticket listing */

    private _ticket_waitmodal = false;
    private async OnTicketModal(_ticket){
        if (!this._ticket_waitmodal){
            this._ticket_waitmodal = true;
            
            const modal = await this.modalCtrl.create({
                component: UiTicketModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    ticket: _ticket
                }                                    
            });

            await modal.present();
    
            setTimeout(() => {
                this._ticket_waitmodal = false;
            }, 100);
        }
    }

    public _selected = null;
    async SelectTicket(_ticket){
        if (!_ticket){
            this._selected = null;
            this.change.markForCheck();
        }
        else {
            this.toast.ShowWait();
            let _tickets = await this.GetTicketItems([ _ticket.objid ]);
            this.toast.HideWait();
    
            if (_tickets && _tickets.length > 0){
                if (_ticket && !_ticket.IsToPay){
                    this.OnTicketModal(_tickets[0]);
                }
                else { // the ticket is pending (and recent)
                    this._selected = _tickets[0];
                    this.change.markForCheck();    
                }    
            }    
        }
    }

    OnTicketAction(action){
        if (['REOPEN', 'PAYMENT', 'CANCEL'].indexOf(action) != -1){
            this.SelectTicket(null);
        }
    }

    private _stepsize = 50;
    private _lastpend = 50;
    private _lastpaid = 50;

    public _hidepend = false;
    public _hidepaid = false;

    private _IsDOMVisible(endpage, scrollable){
        let _endpos = endpage.getBoundingClientRect();
        let _scrpos = scrollable.getBoundingClientRect();

        let _height = _scrpos.top + scrollable.clientHeight;
        return (_endpos.top < _height) && (_endpos.bottom >= 0);
    }

    @ViewChild('lastpend', {static: false}) _endPend: ElementRef;
    OnPendingScroll(event){
        if (this._endPend){
            let _visible = this._IsDOMVisible(this._endPend.nativeElement, event.target);
            if (_visible){
                this._hidepend = (this._lastpend >= this.PendingTickets.length);
                if (!this._hidepend) {
                    this._lastpend += this._stepsize;
                }

                this.change.markForCheck();
            }
        } 
        return false;
    }

    IsPendingLoaded(_ticket){
        return this.PendingTickets.indexOf(_ticket) < this._lastpend;
    }

    @ViewChild('lastpaid', {static: false}) _endPaid: ElementRef;
    OnPaidScroll(event){
        if (this._endPaid){
            let _visible = this._IsDOMVisible(this._endPaid.nativeElement, event.target);
            if (_visible){
                this._hidepaid = (this._lastpaid >= this.PaidTickets.length);
                if (!this._hidepaid) {
                    this._lastpaid += this._stepsize;
                }
                
                this.change.markForCheck();
            }
        } 
        return false;
    }

    IsPaidLoaded(_row){
        return this.PaidTable.indexOf(_row) < this._lastpaid;
    }

    private _proforma_waitmodal = false;
    async OnProformaSelected(_proforma){
        if (!this._proforma_waitmodal){
            this._proforma_waitmodal = true;
            const modal = await this.modalCtrl.create({
                component: UiSummaryModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    payment: _proforma,
                    proforma: true
                }                                    
            });

            await modal.present();
            
            setTimeout(() => {
                this._proforma_waitmodal = false;
            }, 100);
        }
    }

    private _invoice_waitmodal = false;
    async OnPaymentSelected(_payment){
        if (!this._invoice_waitmodal){
            this._invoice_waitmodal = true;
            const modal = await this.modalCtrl.create({
                component: UiSummaryModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    payment: _payment,
                }                                    
            });

            await modal.present();
            
            setTimeout(() => {
                this._invoice_waitmodal = false;
            }, 100);
        }
    }

    OnInvoiceSelected(_ticket){
        this.SelectTicket(_ticket);
    }
}
